import identity from "ramda/es/identity";
import { useEffect, useState } from 'react';
import { first, map } from 'rxjs/operators';
import { skipWhenStateEqual } from '../../../stream/utils';
import useContextOrStorage from '../../stream/useContextOrStorage';
/**
 * Hooks will provide form data with selector option. Always use memoized selector function !!.
 *
 * @documented
 * @category State
 * @import import useFormData from '@gef-ui/form-core/hooks/state/useFormData'
 * @param {Function} [selector=identity] selector which can take part of formData.
 * @param {*} [initialData=null] - initial state data for useState. This is not default data, for selector.
 * @param {String} [formName] - when used, formStream$ is taken from stream storage instead of context
 * @param {Function} [hasChanged] - function (be careful that its memoized) used to determine whether previous state is equal or not
 * @returns {Object} formData slice - or initialData if form is not yet registered
 * @example
 *
 * 	const wholeFormData = useFormData();
 *
 *  const minionsProp = prop('minions'); // define this outside of render !
 * 	const minions = useFormData(minionsProp);
 *
 * 	// always use memoized selector function for dynamic data
 * 	const dynamicSelector = useMemo(() => prop(externalValue), [externalValue]);
 * 	const someData = useFormData(dynamicSelector);
 *
 */ const useFormData = (selector = identity, initialData = null, formName, hasChanged)=>{
    const [data, setData] = useState(initialData);
    const { formData$ } = useContextOrStorage(formName);
    useEffect(()=>{
        if (formData$) {
            // one time subscription for initial data
            formData$.pipe(map(selector), first()).subscribe(setData);
            const sub = formData$.pipe(skipWhenStateEqual(hasChanged), map(selector)).subscribe(setData);
            return ()=>{
                if (sub) {
                    sub.unsubscribe();
                }
            };
        }
    }, [
        hasChanged,
        formData$,
        selector
    ]);
    return data;
};
export default useFormData;
